
import { useEffect } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SectionTitle } from "@/components/SectionTitle";
import { CTASection } from "@/components/CTASection";
import { Button } from "@/components/ui/button";
import { TreatmentServiceCard } from "@/components/TreatmentServiceCard";
import { FAQAccordion } from "@/components/FAQAccordion";
import { ServiceTestimonials } from "@/components/ServiceTestimonials";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { PageTitle } from "@/components/PageTitle";

const TeethCleaning = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // FAQs specific to Teeth Cleaning
  const faqs = [
    {
      question: "How often should I have my teeth professionally cleaned?",
      answer: "We generally recommend professional teeth cleaning every six months for most patients at Asrani Dental in Sadar, Nagpur. However, some individuals may need more frequent cleanings, such as those with gum disease, heavy tartar buildup, or certain medical conditions like diabetes. During your check-up, we'll recommend the ideal cleaning schedule for your specific needs."
    },
    {
      question: "What happens during a professional teeth cleaning?",
      answer: "At our Nagpur clinic, professional teeth cleaning includes several steps: examination of your oral health; removal of plaque and tartar using specialized instruments; professional polishing with a gritty toothpaste to remove surface stains; expert flossing to remove interdental debris; rinsing to eliminate any remaining particles; and fluoride treatment if appropriate. We also provide personalized oral hygiene instructions."
    },
    {
      question: "Does professional teeth cleaning hurt?",
      answer: "Most patients at Asrani Dental, Sadar find professional teeth cleaning to be comfortable with minimal discomfort. Some sensitivity might be experienced if you have gum inflammation, exposed roots, or heavy tartar buildup. Our gentle approach and modern techniques minimize any discomfort. For patients with dental anxiety or sensitivity, we offer options to make the experience more comfortable."
    },
    {
      question: "What's the difference between a regular cleaning and a deep cleaning?",
      answer: "A regular cleaning (prophylaxis) at our Nagpur clinic focuses on cleaning the surfaces of the teeth and just below the gumline. It's primarily preventive maintenance for patients with good oral health. A deep cleaning (scaling and root planing) is a therapeutic procedure for patients with gum disease that involves cleaning beneath the gumline down to the tooth roots and smoothing the root surfaces to promote healing and gum reattachment."
    },
    {
      question: "Can professional teeth cleaning whiten my teeth?",
      answer: "Professional teeth cleaning at Asrani Dental in Nagpur can remove surface stains and reveal the natural color of your teeth, making them appear brighter. However, it won't change the intrinsic color of your teeth. For significant whitening, we offer professional teeth whitening services that can be discussed during your visit. Many patients opt for a whitening treatment after a professional cleaning for optimal results."
    }
  ];

  // Testimonials for Teeth Cleaning
  const testimonials = [
    {
      name: "Anil Patil",
      comment: "I always avoided dental cleanings until I found Asrani Dental in Sadar, Nagpur. Their hygienist is incredibly gentle yet thorough. For the first time, I actually enjoyed a dental cleaning! My teeth feel amazingly clean, and the entire staff makes me feel comfortable and welcomed.",
      rating: 5
    },
    {
      name: "Shilpa Joshi",
      comment: "Regular cleanings at Asrani Dental Clinic in Nagpur have completely transformed my oral health. After years of struggling with gingivitis, their detailed cleaning approach and personalized home care instructions have helped me maintain healthy gums. The difference is remarkable, and I actually look forward to my cleaning appointments now.",
      rating: 5
    },
    {
      name: "Rahul Deshmukh",
      comment: "As someone with sensitive teeth, I was always anxious about cleanings. The team at Asrani Dental in Nagpur is so attentive to my comfort level, taking breaks when needed and using special techniques to minimize sensitivity. They've shown me that professional cleaning doesn't have to be uncomfortable. My teeth look and feel great after every visit!",
      rating: 5
    }
  ];

  const handleBookAppointment = () => {
    window.open("https://www.healthcoco.com/nagpur/doctor/dr-hemant-asrani-endodontist-dentist/USRH249D644", "_blank");
  };

  return (
    <div className="min-h-screen">
      <PageTitle title="Teeth Cleaning in Nagpur | Cost of Teeth Cleaning in Nagpur" />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 bg-dental-offwhite">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block text-dental-highlight font-medium mb-4 uppercase tracking-wider text-sm">
              Our Services
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-semibold mb-6">
              Teeth Cleaning in Nagpur
            </h1>
            <p className="text-lg text-dental-muted">
              Professional teeth cleaning and oral hygiene services at Asrani Dental, Sadar, Nagpur for a healthier smile.
            </p>
            <div className="mt-8">
              <Button className="btn-primary" onClick={handleBookAppointment}>
                Book Appointment
              </Button>
            </div>
          </div>
        </div>
      </section>
      
      {/* Services Overview */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="/lovable-uploads/teethcleaning.jpg" 
                alt="Teeth Cleaning at Asrani Dental Clinic, Sadar, Nagpur" 
                className="rounded-xl shadow-soft w-full h-auto"
              />
            </div>
            
            <div>
              <SectionTitle 
                title="Professional Teeth Cleaning" 
                subtitle="Teeth Cleaning"
                description="At Asrani Dental in Sadar, Nagpur, our professional teeth cleaning services remove plaque and tartar buildup to maintain optimal oral health and prevent dental problems."
                alignment="left"
              />
              
              <div className="mt-8 space-y-4">
                <div className="flex items-start">
                  <CheckCircle2 size={24} className="text-dental-highlight mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-medium mb-1">Thorough Cleaning</h3>
                    <p className="text-dental-muted">Professional removal of plaque and tartar that regular brushing and flossing can't reach.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle2 size={24} className="text-dental-highlight mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-medium mb-1">Disease Prevention</h3>
                    <p className="text-dental-muted">Regular cleanings help prevent gum disease, tooth decay, and other oral health issues.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle2 size={24} className="text-dental-highlight mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-medium mb-1">Fresh Breath</h3>
                    <p className="text-dental-muted">Elimination of bacteria and food particles that can cause bad breath.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle2 size={24} className="text-dental-highlight mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-medium mb-1">Early Detection</h3>
                    <p className="text-dental-muted">Comprehensive examination to identify potential issues before they become serious problems.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Treatments Section */}
      <section className="py-16 md:py-24 bg-dental-cream">
        <div className="container-custom">
          <SectionTitle 
            title="Our Teeth Cleaning Services" 
            subtitle="Services We Offer"
            description="At Asrani Dental in Sadar, Nagpur, we provide comprehensive cleaning and preventive care services for all ages."
            alignment="center"
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            <TreatmentServiceCard 
              title="Regular Prophylaxis"
              description="Standard professional cleaning to remove plaque, tartar, and stains from all surfaces of the teeth."
              imageUrl="/lovable-uploads/79bf0209-ea1e-4839-a4a8-34dc4533acb6.png"
            />
            
            <TreatmentServiceCard 
              title="Deep Cleaning (Scaling & Root Planing)"
              description="Therapeutic cleaning for patients with gum disease, reaching below the gumline to clean root surfaces."
              imageUrl="/lovable-uploads/285110c2-97b3-4b07-a330-a8bbc29b8e1c.png"
            />
            
            <TreatmentServiceCard 
              title="Periodontal Maintenance"
              description="Ongoing care for patients with a history of gum disease to prevent recurrence and maintain gum health."
              imageUrl="/lovable-uploads/a584a4c0-a632-450f-8e90-7280ee743164.png"
            />
            
            <TreatmentServiceCard 
              title="Fluoride Treatment"
              description="Protective application that strengthens tooth enamel and helps prevent decay, especially beneficial for children."
              imageUrl="/lovable-uploads/c406f99e-ae7c-4b89-b40f-800c4f2f76f6.png"
            />
            
            <TreatmentServiceCard 
              title="Oral Hygiene Instructions"
              description="Personalized guidance on proper brushing, flossing, and other home care techniques for optimal oral health."
              imageUrl="/lovable-uploads/833b4b48-2be3-4694-94b8-bc2299fb8d86.png"
            />
            
            <TreatmentServiceCard 
              title="Dental Sealants"
              description="Protective coating applied to the chewing surfaces of back teeth to prevent decay in the deep grooves and fissures."
              imageUrl="/lovable-uploads/41dbeea4-047f-44f4-8002-7151462d749f.png"
            />
          </div>
        </div>
      </section>
      
      {/* Testimonials Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-custom">
          <SectionTitle 
            title="Patient Testimonials" 
            subtitle="What Our Patients Say"
            description="Hear from our patients about their teeth cleaning experiences at Asrani Dental in Sadar, Nagpur."
            alignment="center"
          />
          
          <div className="max-w-4xl mx-auto mt-12">
            <ServiceTestimonials testimonials={testimonials} />
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-dental-offwhite">
        <div className="container-custom">
          <SectionTitle 
            title="Frequently Asked Questions" 
            subtitle="Common Questions"
            description="Find answers to common questions about teeth cleaning at Asrani Dental, Nagpur."
            alignment="center"
          />
          
          <div className="max-w-3xl mx-auto mt-12">
            <FAQAccordion faqs={faqs} />
          </div>
        </div>
      </section>
      
      {/* Next Steps Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-serif font-semibold mb-6">Ready for a Healthier Smile?</h2>
            <p className="text-lg text-dental-muted mb-8">
              Contact Asrani Dental in Sadar, Nagpur today to schedule your professional teeth cleaning appointment. Regular cleanings are essential for maintaining optimal oral health.
            </p>
            
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button className="btn-primary flex items-center text-base group" onClick={handleBookAppointment}>
                Book Appointment
                <ArrowRight size={18} className="ml-2 transition-transform duration-300 group-hover:translate-x-1" />
              </Button>
              
              <Button className="btn-secondary flex items-center text-base" onClick={() => window.location.href = '/contact'}>
                Contact Us
              </Button>
            </div>
            
            <div className="mt-10">
              <p className="text-dental-muted mb-4">Interested in our other dental services in Nagpur?</p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/services/general-dentistry" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  General Dentistry
                </Link>
                <Link to="/services/tooth-whitening" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  Tooth Whitening
                </Link>
                <Link to="/services/cosmetic-dentistry" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  Cosmetic Dentistry
                </Link>
                <Link to="/services/painless-rct-in-nagpur" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  Painless RCT
                </Link>
                <Link to="/services/pediatric-dentistry" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  Pediatric Dentistry
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <CTASection
        title="Maintain Your Oral Health"
        description="At Asrani Dental in Sadar, Nagpur, our professional teeth cleaning services help prevent dental problems and maintain a healthy smile. Schedule your appointment today."
        primaryButtonText="Book an Appointment"
        secondaryButtonText="Learn More"
        primaryButtonAction={handleBookAppointment}
        secondaryButtonAction={() => window.location.href = "/about"}
      />
      
      <Footer />
    </div>
  );
};

export default TeethCleaning;
